import React from "react";

const PrivacyPolicy = () => {
  return (
    <>
      <div className="min-h-screen mt-[86px] bg-white py-10 px-4 flex justify-center">
        <div className="w-full max-w-4xl">
          <h1 className="text-center text-[#5b21b6] font-semibold text-xl mb-2">
            Preventive Pulse
          </h1>
          <h2 className="text-center text-[#13202C] font-extrabold text-5xl mb-6">
            Privacy Policy
          </h2>
          <div className="border border-[#13202C] p-8 rounded-md space-y-4 text-slate-700">
            <p>
              At <strong>Preventive Pulse</strong> (“we”, “us”, “our”), we respect the
              privacy of our users (“you”, “your”). This Privacy Policy outlines how
              we collect, use, disclose, and protect your personal information when
              you access and use our website (the “Website”) and our SaaS-based
              healthtech infrastructure (the “Service”). We value the trust you place
              in us and maintain the highest security standards to protect your
              information.
            </p>
            <p>
              This Privacy Policy (hereinafter referred to as{" "}
              <strong>“Privacy Policy”</strong>) specifies the manner in which
              personal and/or sensitive information is collected, received, stored,
              processed, disclosed, and handled by Preventive Pulse. It does not apply to
              third-party services or websites that you may access through our Online
              Platforms.
            </p>
            <p>
              This Privacy Policy shall be governed in accordance with the provisions
              of the{" "}
              <em className="font-semibold">Information Technology Act, 2000</em> and
              rules made thereunder, including the{" "}
              <em className="font-semibold">
                Information Technology (Reasonable security practices and procedures
                and sensitive personal data or information) Rules, 2011
              </em>{" "}
              and applicable laws.
            </p>
            <p>
              Please read this Privacy Policy carefully before using, signing up, or
              accessing our services on the Online Platforms.
            </p>
            <ul className="list-disc ml-6 space-y-2">
              <li>
                By using the <strong>Preventive Pulse</strong> website and registering at{" "}
                <strong>Preventive Pulse.in</strong>, you consent to be contacted via email
                or phone.
              </li>
              <li>
                You authorize <strong>Preventive Pulse</strong>, its affiliates, and partners
                to offer services, products, and promotional offers.
              </li>
              <li>
                Personally identifiable information may be collected for promotional
                purposes and analytics.
              </li>
              <li>
                This applies even if you are registered under DND, DNC, or NCPR.
              </li>
              <li>
                This policy applies to all data collected via website, app, or
                services provided by Preventive Pulse.
              </li>
            </ul>
            <h3 className="text-lg font-semibold text-[#64748B]mt-6">
              Scope of this Policy
            </h3>
            <p>
              Any user who visits our Online Platforms or avails our Services is
              covered under this Privacy Policy. This includes all forms of data,
              whether stored digitally or physically.
            </p>
            <h3 className="text-lg font-semibold text-[#64748B] mt-6">
              Information Collected
            </h3>
            <p>
              We collect information necessary to operate our business and deliver our
              Services. This includes personally identifiable information like name,
              contact details, identification, KYC documents, biometrics, payment
              details, and technical data from devices.
            </p>
            <ol className="list-decimal ml-6 space-y-2">
              <li>
                <strong>Information You Give Us</strong>
                <p className="mt-1">
                  You may be asked to provide information while using our services or
                  contacting us via phone, email, or chat. This may include:
                </p>
              </li>
              <li>
                Personal details such as date of birth, age, marital status, place of
                birth, nationality, mother tongue, photographs, and government-issued
                IDs like Aadhar, PAN.
              </li>
            </ol>
            <ul className="list-disc ml-6 space-y-2">
              <li>
                Contact information including name, address, phone number, and email
                address.
              </li>
            </ul>
            <ol className="list-decimal ml-6 space-y-2" start={3}>
              <li>
                Other information that Preventive Pulse is required to collect as per
                specific mandate from partner bank or as a requirement under
                applicable law, regulation, rule, or statute in India.
              </li>
            </ol>
            <p>
              We may also require additional information about you for performing
              ongoing due diligence (necessitated under the applicable law) based on
              your risk assessment, transaction value, etc.
            </p>
            <p>
              You must ensure that all your data and/or information shared with us is
              accurate, correct, and complete at all times. You understand that you
              must promptly inform us of any changes to your data and/or information
              which may impact your use of our Services. We shall not be liable or
              responsible for any loss or damage that may occur to you or other third
              parties on account of provision of incorrect data and/or information
              provided by a user.
            </p>
            <ol className="list-decimal ml-6 space-y-2">
              <li>
                <strong>Information We Collect About You</strong>
              </li>
            </ol>
            <p>
              With regard to each of your visits to any of the Online Platforms, we
              may automatically collect and analyse the following demographic and
              other information:
            </p>
            <ol className="list-decimal ml-6 space-y-2">
              <li>
                Device Information: Information obtained with consent from your mobile
                phone or other access device. It includes device ID, device type, geo
                location information, connection information, mobile network
                information, transactional and promotional SMS, communication
                information including contacts and call logs.
              </li>
              <li>
                Technical Information: Information including the Internet Protocol
                (IP) address used to connect your device to the Internet, your login
                information, browser type and version, time zone setting, browser
                plug-in types and versions, operating system and platform and other
                similar information.
              </li>
            </ol>
            <ul className="list-disc ml-6 space-y-2">
              <li>
                Access Information: Information including the full Uniform Resource
                Locators (URL) clickstream to, through and from the Online Platforms
                including date and time; information about transactions and trades
                executed or attempted to be executed by you; material or information
                that you viewed or searched for; page response times, download errors,
                length of visits to certain pages, page interaction information
                including scrolling, clicks, and mouse-overs, and methods used to
                browse away from the page and any phone number used to call our
                customer service number.
              </li>
            </ul>
            <ol className="list-decimal ml-6 space-y-2">
              <li>
                Transactional Information: For enabling you to make payments while
                accessing Services through our Online Platforms, we may store your
                financial information such as your bank account or details mentioned
                on your credit cards or debit cards, or other payment instrument
                details etc. that are required for a successful transaction. We also
                store transaction details such as amount spent, counterpart details,
                beneficiary information, account balance, transaction ID, date and
                time of transaction. Further, we may also store information relating
                to your income, lifestyle levels and usage pattern.
              </li>
              <li>
                Correspondence Information: For quality and training purposes or for
                its own protection, Preventive Pulse may monitor or record its telephone
                conversations with you or anyone acting on your behalf. By
                communicating with Preventive Pulse, you acknowledge that your communication
                may be overheard, monitored, or recorded without further notice or
                warning.
              </li>
              <li>
                Cookies: We also collect anonymous information through our use of
                cookies and web beacons. We may collect and store any information you
                provide us when you use our Services, including when you add
                information on a web form, add or update your account information,
                participate in community discussions, chats, or dispute resolutions,
                or when you otherwise correspond with us regarding our Services.
              </li>
            </ol>
            <ol className="list-decimal ml-6 space-y-2">
              <li>
                <strong>Information We Receive from Other Sources</strong>
              </li>
            </ol>
            <p>
              We may receive information about you if you use any of the other
              websites/apps we operate or the other services we provide. Such data may
              be shared internally and combined with data collected through use of the
              Online Platforms. We also work closely with select third parties
              including, for instance, subcontractors such as advertising networks,
              analytics providers, search information providers and may receive
              information about you from such sources. We collect non-personally
              identifiable information to track the total number of visitors to the
              Online Platforms in aggregate form and identify the type of internet web
              browser and operating system used by the visitor. This information
              allows us continuously to improve the Online Platforms and Services.
            </p>
            <p>
              <strong>Express Consent:</strong>
            </p>
            <p>
              You also specifically agree and consent to us and our group or affiliate
              companies collecting, storing, processing, transferring and sharing
              information (including Personal Information) related to you with third
              parties such as banks, financial institutions, credit information
              companies, entities registered under applicable laws solely for the
              purpose of reviewing your profile and processing your transaction
              requests for the Services or for such other products/services offered by
              us or our group, affiliate or associates.
            </p>
            <p>
              In addition to the above, we identify and use your IP address to help
              diagnose problems with our server, resolve such problems and administer
              the Online Platforms. Your IP address is also used to help identify you
              and to gather broad demographic information.
            </p>
            <p>
              We use this information to tailor your experience at the Online
              Platforms, providing you with content that we think you might be
              interested in and to display content according to your preferences. We
              use your Personal Information to send you promotional emails, however,
              we will provide you the ability to opt-out of receiving such emails from
              us. If you opt out, the Company may still send you non-promotional
              emails, such as emails about the Services and your Account on the Online
              Platforms. Unless and until, you explicitly give your consent to us, to
              do so, we will not share your Personal Information with another user of
              the Online Platforms and vice versa.
            </p>
            <p>
              <strong>User Access of Personal Information</strong>
            </p>
            <p>
              As a registered Preventive Pulse user, you can modify some of your Personal
              Information and your privacy preferences by accessing the “Account”
              section of the Online Platforms.
            </p>
            <p>
              <strong>How We Use Your Information</strong>
            </p>
            <p>
              Our primary purpose in collecting Personal Information is to provide you
              with a secure, smooth, efficient, and customized experience. We may use
              your Personal Information for the following:
            </p>
            <ol className="list-decimal ml-6 space-y-2">
              <li>
                to provide and improve the Services on the Online Platforms that you
                request;
              </li>
              <li>to resolve disputes and troubleshoot problems;</li>
              <li>
                to help promote a safe service on the Online Platforms and protect the
                security and integrity of the Online Platforms, the Services and the
                users;
              </li>
              <li>to collect money from you in relation to the Services;</li>
              <li>
                to inform you about online and offline offers, products, services, and
                updates;
              </li>
              <li>
                to customize your experience on the Online Platforms or share
                marketing material with you;
              </li>
              <li>
                to detect, prevent and protect us from any errors, fraud and other
                criminal or prohibited activity on the Online Platforms;
              </li>
              <li>to enforce and inform about our terms and conditions;</li>
              <li>
                to process and fulfil your request for Services or respond to your
                comments, and queries on the Online Platforms;
              </li>
              <li>to contact you;</li>
              <li>
                to allow our business partners and/or associates to present customised
                messages to you;
              </li>
              <li>
                to communicate important notices or changes in the Services, use of
                the Online Platforms and the terms/policies which govern the
                relationship between you and the Company and with our affiliates for
                providing Services to you;
              </li>
              <li>
                for any other purpose after obtaining your consent at the time of
                collection.
              </li>
            </ol>
            <p>
              Further, we may use the data and Information collected/stored as
              mentioned above for providing or improving our Services, improving user
              experience, risk checks, strengthening our security checks, for analysis
              and for offering you new features in the existing Services you are
              availing or offering altogether new product/service which may be
              relevant for you. We collect and use some of your information in an
              aggregated form to compile statistical and demographic profiles for our
              business and marketing activities and to customise our Services to you.
            </p>
            <p>
              <strong>Disclosure of Information</strong>
            </p>
            <p>
              Information security is critical to us and we will not share your
              Information with any other third parties except, and only to the extent
              necessary, after duly adhering to the confidentiality obligations
              consistent with this Privacy Policy:
            </p>
            <p>
              We may make your Personal Information and/or other Information available
              to our group entities/affiliates to enable providing you Services
              through the Online Platforms. Please note that all information shared
              with our group entities or affiliates or made available to our group
              entities or affiliates will be governed by the terms of this Privacy
              Policy.
            </p>
            <p>
              We may also disclose your Personal Information to third party vendors,
              consultants, and other service providers who work for the Company, who
              are bound by contractual obligations to keep such personal information
              confidential and use it only for the purposes for which we disclose it
              to them. This disclosure may be required for us, for instance, to
              provide you access to our Services and process payments including
              validation of your bank accounts, to facilitate and assist our marketing
              and advertising activities/initiatives, for undertaking auditing or data
              analysis, or to prevent, detect, mitigate, and investigate fraudulent or
              illegal activities related to our Services. We do not disclose your
              Personal Information to third parties for their marketing and
              advertising purposes without your explicit consent.
            </p>
            <p>
              The Preventive Pulse may disclose your information, to the extent necessary:
            </p>
            <ol className="list-decimal ml-6 space-y-2">
              <li>
                to comply with laws and to respond to lawful requests and legal
                process,
              </li>
              <li>
                to protect the rights and property of the Company, our users, and
                others, including to enforce the terms, and
              </li>
            </ol>
            <ul className="list-disc ml-6 space-y-2">
              <li>
                in an emergency to protect the personal safety and assets of the
                Company, the users, or any person.
              </li>
            </ul>
            <p>
              In such an event, Preventive Pulse is in no manner responsible for informing
              you or seeking your approval or consent. We and our group entities or
              affiliates (to the extent Personal Information is collected by them)
              may, in compliance with applicable laws, share/transfer/assign all of
              your Personal Information and other information with any other business
              entities, in the event of a merger, sale, re-organization, amalgamation,
              joint ventures, assignment, restructuring of business or transfer or
              disposition of all or any portion of our business.
            </p>
            <h3 className="text-lg font-semibold text-[#64748B] mt-6">
              Data Retention
            </h3>
            <p>
              Preventive Pulse may, at its sole discretion, retain any information and/or
              data provided by you for as long as your Account is active, for
              resolution of disputes, for the purpose of investigations or ongoing
              prosecutions or in case of any suspicious/illegal transactions, for
              enforcement of any agreements, and/or as necessitated under applicable
              laws. The retention period may be extended in accordance with the
              applicable law. We make no warranties and shall not be liable regarding
              non-availability or non-retention of any information and/or data
              provided by you beyond the deletion of your Account.
            </p>
            <h3 className="text-lg font-semibold text-[#64748B] mt-6">
              Security Practices
            </h3>
            <p>
              Preventive Pulse employs a range of measures to protect the privacy and
              confidentiality of Personal Information. We primarily hold Personal
              Information in electronic form and take reasonable steps to ensure your
              Personal Information is protected from misuse and loss and from
              unauthorised access, modification or disclosure.
            </p>
            <p>
              Our Online Platforms and systems are firewall protected. Additionally,
              the servers on which we store personal information are kept in a secure
              environment. Information in our data centres, including Personal
              Information, is encrypted in accordance with applicable laws. Further,
              we have implemented applicable industry standard security controls
              including PCI DSS that help us protect all our customer’s information in
              a highly secure manner. Also, Personal Information is destroyed or
              de-identified when no longer needed.
            </p>
            <p>
              No data transmission over the internet is fully secure, so we cannot
              ensure or warrant the security of any Personal Information you submit to
              us. Further, we do not guarantee in any way, the security of any
              Personal Information that you transmit or share on the Online Platforms.
              Accordingly, any Personal Information or other information which you
              transmit to us online is transmitted at your own risk.
            </p>
            <p>
              If any security breach comes to our knowledge, then we may take all
              steps required to protect misuse of such Information and may attempt to
              notify you electronically so that you can take appropriate protective
              steps.
            </p>
            <h3 className="text-lg font-semibold text-[#64748B] mt-6">Cookies</h3>
            <p>
              A cookie is a small piece of information stored by a web server on a web
              browser so it can be later read back from that browser. We use cookie
              and tracking technology depending on the features offered. No Personal
              Information will be collected via cookies and other tracking technology;
              however, if you previously provided personally identifiable information,
              cookies may be tied to such information. Aggregate cookie and tracking
              information may be shared with third parties.
            </p>
            <h3 className="text-lg font-semibold text-[#64748B] mt-6">
              Links to Other Sites
            </h3>
            <p>
              The procedures and practices set forth in this Privacy Policy describe
              the use and disclosure of information collected only through the Online
              Platforms. These Online Platforms may contain links to other websites.
              Any Personal Information about you collected whilst visiting such
              websites is not governed by this Privacy Policy. You are encouraged to
              exercise caution and review the privacy policies applicable to such
              websites before accessing them. You acknowledge that Preventive Pulse shall not
              be responsible for and has no control over the practices and content of
              any website accessed using the links contained on our Online Platforms.
            </p>
            <h3 className="text-lg font-semibold text-[#64748B] mt-6">
              Changes to this Privacy Policy
            </h3>
            <p>
              We reserve the right to amend this Privacy Policy from time to time to
              ensure that this Privacy Policy is consistent with any developments to
              the way we use your Personal Information or any changes to the laws and
              regulations applicable to us. We will make available the updated Privacy
              Policy on the Online Platforms. You are encouraged to visit the Online
              Platforms from time to time to ensure that you are well informed of our
              latest policies in relation to personal data protection. All
              communications, transactions and dealings with us shall be subject to
              the latest version of this Privacy Policy in force at the time.
            </p>
            <p>
              This Privacy Policy applies in conjunction with any other notices,
              contractual clauses and consent clauses that apply in relation to the
              collection, use and disclosure of your personal data by us. We may
              revise this Privacy Policy from time to time without any prior notice.
              You may determine if any such revision has taken place by referring to
              the date on which this Privacy Policy was last updated.
            </p>
            <h3 className="text-lg font-semibold text-[#64748B] mt-6">
              Customer Support
            </h3>
            <p>
              If you have any questions about this Privacy Policy, please contact us
              at:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li>
                <strong>Email :</strong>preventivepulsehealth@gmail.com
              </li>
              <li>
                <strong>Phone:</strong> 9899761818
              </li>
              <li>
                <strong>Support Hours:</strong> Mon – Sat, 10:00 am to 6:00 pm
              </li>
              <strong>Website:</strong>www.preventivepulse.in
              <p>We’re here to help you every step of the way.</p>
            </ul>
          </div>
        </div>
      </div>

    </>
  );
};

export default PrivacyPolicy;
